import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Combobox,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  StatTile,
  Textarea,
  Toolbar,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { Th } from './parts.js';
import { MasterTable, Messages, Tiles, today, useAction } from './document.js';
import {
  apiSend,
  useResource,
  type AccountLinkRow,
  type AccountLinkWriteResult,
  type AccountLinks,
  type AdminWriteResult,
  type ApiAccount,
  type BranchRow,
  type CompanyProfile,
  type CurrencyRow,
  type FiscalYearRow,
  type FxRateRow,
  type NumberSeriesRow,
  type PeriodRow,
  type SetupRefs,
  type TaxCodeRow,
  type UomRow,
} from '../lib/api.js';
import { allUoms } from '../lib/reference-data.js';

/**
 * Setup — the book's own settings, and the masters everything else is built on.
 *
 * Eight screens behind eight nav keys, one file, because they share the property
 * that separates them from every operational screen: **they are set once and
 * then depended upon.** A voucher is wrong for a day; a fiscal year with the
 * wrong end date is wrong for every report anybody runs afterwards. So these
 * screens are deliberately unlike the registers — no scan bar, no line grid, no
 * keyboard shortcut to post — and they say what a change costs before it is
 * made.
 *
 * `App` renders the whole module only for a session holding `admin.company`,
 * which `seed/0004_access.sql` grants to the super administrator and revokes
 * from every other role. That includes the administrator, who runs the rest of
 * the application: an administrator manages users, zones and every operational
 * module, and does not redefine what a year, a currency or a unit is.
 *
 * **Three things cannot be changed here at all**, and in each case the database
 * is the one refusing rather than these screens:
 *
 *   · base currency once a voucher has posted, and valuation method once stock
 *     has moved — `trg_companies_policy_lock`;
 *   · a numbering series wound back past a number already issued;
 *   · a tax rate that documents already carry — that is a new row with a later
 *     effective date, which is why `tax_codes_code_uq` includes it.
 *
 * Each screen greys the field and says why, so nobody types into a box that is
 * going to be refused.
 */

export type SetupKey =
  'company' | 'fy' | 'numbering' | 'fx' | 'branches' | 'currencies' | 'uoms' | 'links';

export function SetupScreen({ section }: { section: SetupKey }) {
  switch (section) {
    case 'fy':
      return <PeriodsScreen />;
    case 'numbering':
      return <NumberingScreen />;
    case 'fx':
      return <TaxAndFxScreen />;
    case 'branches':
      return <BranchesScreen />;
    case 'currencies':
      return <CurrenciesScreen />;
    case 'uoms':
      return <UomsScreen />;
    case 'links':
      return <LinkAccountsScreen />;
    default:
      return <CompanyProfileScreen />;
  }
}

// ── Company profile ──────────────────────────────────────────────────────────

function CompanyProfileScreen() {
  const res = useResource<{ company: CompanyProfile; canEdit: boolean }>('/setup/company', auth());
  const refsRes = useResource<SetupRefs>('/setup/refs', auth());
  const company = res.data?.company;
  const { busy, notice, problem, run } = useAction(res.reload);

  const [draft, setDraft] = useState<CompanyProfile | null>(null);
  useEffect(() => setDraft(null), [res.data]);

  const editing = draft !== null;
  const shown = draft ?? company;

  if (res.loading) return <SkeletonRows rows={8} />;
  if (res.error) {
    return (
      <Callout tone="danger" title="That did not load">
        {res.error}
      </Callout>
    );
  }
  if (!shown || !company) return <EmptyState title="No company" description="Run pnpm db:seed." />;

  const set = (patch: Partial<CompanyProfile>) => setDraft({ ...shown, ...patch });

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Company Profile"
        title={shown.legalName}
        description="Your company's details and the settings every screen uses."
      />

      {/*
        Where this ends up, said once and on the screen that edits it.

        Every one of these used to be somewhere else as well: the top bar
        carried a literal name, mark and tagline, and a printed report carried
        no company at all. Naming the destinations here is what makes this a
        control panel rather than a form somebody fills in and never sees again.
      */}
      <Callout tone="info" title="Where these appear">
        <strong>Logo, name and tagline</strong> head the top bar, beside the application clock.{' '}
        <strong>Logo, legal name, address, phone and email</strong> print as the letterhead above
        every report and on every voucher printed from an entry screen, and head every Excel export.{' '}
        <strong>NTN, STRN and CUIN</strong> print beside them — each one only while its box below is
        ticked, which is how a book that is not sales-tax registered keeps its STRN off the paper
        without deleting it. The <strong>report footer</strong> prints under all of it. Nothing here
        is duplicated anywhere else — change it once and every screen follows.
      </Callout>

      <Messages notice={notice} problem={problem} />

      {(company.hasPostings || company.hasStockMovement) && (
        <Callout tone="warn" title="Some of this is now history rather than settings">
          {company.hasPostings && (
            <>
              Vouchers have posted, so the <strong>base currency</strong> is fixed: every posted
              amount carries a base figure computed against it, and changing it now would not
              convert the history, it would relabel it.{' '}
            </>
          )}
          {company.hasStockMovement && (
            <>
              Stock has moved, so the <strong>valuation method</strong> is fixed — changing it would
              restate every historical COGS figure.
            </>
          )}
        </Callout>
      )}

      {/*
        Edit / Cancel / Save belong to the page, not to a card.
        One Save writes all three cards — identity, address and policy are one
        UPDATE — so putting the button inside "Books" both implied it saved only
        that card and squeezed a third-width card header until its subtitle
        wrapped a word per line.
      */}
      <div className="flex justify-end gap-2 pb-3.5" data-noprint="1">
        {editing ? (
          <>
            <Button size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              onClick={() =>
                void run(async () => {
                  const result = await apiSend<AdminWriteResult>(
                    'PATCH',
                    '/setup/company',
                    shown,
                    auth(),
                  );
                  setDraft(null);
                  return result;
                })
              }
            >
              Save
            </Button>
          </>
        ) : (
          <Button size="sm" disabled={!res.data?.canEdit} onClick={() => setDraft(company)}>
            Edit
          </Button>
        )}
      </div>

      <div className="grid gap-3.5 lg:grid-cols-3">
        <Card flush>
          <CardHeader
            title="Identity"
            subtitle="Statutory registration, and the name the application wears"
          />
          <CardBody className="flex flex-col gap-2.5">
            <Row
              label="Company name"
              edit={editing}
              value={shown.name}
              onChange={(v) => set({ name: v })}
              mono
            />
            <Row
              label="Legal name"
              edit={editing}
              value={shown.legalName}
              onChange={(v) => set({ legalName: v })}
            />
            <Row
              label="Tagline"
              edit={editing}
              value={shown.tagline}
              onChange={(v) => set({ tagline: v })}
            />
            <Row
              label="Logo URL"
              edit={editing}
              value={shown.logoUrl}
              onChange={(v) => set({ logoUrl: v })}
              mono
            />
            <Row
              label="Brand mark"
              edit={editing}
              value={shown.brandMark}
              onChange={(v) => set({ brandMark: v })}
              mono
            />
            <Row
              label="NTN"
              edit={editing}
              value={shown.ntn}
              onChange={(v) => set({ ntn: v })}
              mono
            />
            <Row
              label="STRN"
              edit={editing}
              value={shown.strn}
              onChange={(v) => set({ strn: v })}
              mono
            />
            <Row
              label="CUIN"
              edit={editing}
              value={shown.cuin}
              onChange={(v) => set({ cuin: v })}
              mono
            />

            {/*
              Which of the three reaches paper.

              A registration number is required on a tax invoice and is noise on
              an internal trial balance, and a company that is not sales-tax
              registered holds an STRN it must not print — so it is a decision,
              and this is where the three numbers already live. It governs the
              letterhead on every printed document and report and the heading
              block on every Excel export; the screen and this form still show
              all three, because you cannot decide whether to print a number you
              cannot see, and unticking one withholds it rather than clearing it.
            */}
            <div className="mt-1 border-t border-[var(--line-2)] pt-2.5">
              <p className="bb-eyebrow m-0 pb-2">Show on print and export</p>
              {editing ? (
                <div className="flex flex-col gap-1.5">
                  <Checkbox
                    checked={shown.printShowNtn}
                    onChange={(e) => set({ printShowNtn: e.target.checked })}
                    label="NTN"
                  />
                  <Checkbox
                    checked={shown.printShowStrn}
                    onChange={(e) => set({ printShowStrn: e.target.checked })}
                    label="STRN"
                  />
                  <Checkbox
                    checked={shown.printShowCuin}
                    onChange={(e) => set({ printShowCuin: e.target.checked })}
                    label="CUIN"
                  />
                </div>
              ) : (
                <Row
                  label="Printed identifiers"
                  value={
                    [
                      shown.printShowNtn && shown.ntn ? 'NTN' : null,
                      shown.printShowStrn && shown.strn ? 'STRN' : null,
                      shown.printShowCuin && shown.cuin ? 'CUIN' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'none'
                  }
                />
              )}
            </div>
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader title="Address & contact" subtitle="Printed on every statutory document" />
          <CardBody className="flex flex-col gap-2.5">
            <Row
              label="Address"
              edit={editing}
              value={shown.addressLine1}
              onChange={(v) => set({ addressLine1: v })}
            />
            <Row
              label="Address (2)"
              edit={editing}
              value={shown.addressLine2}
              onChange={(v) => set({ addressLine2: v })}
            />
            <Row
              label="City"
              edit={editing}
              value={shown.city}
              onChange={(v) => set({ city: v })}
            />
            <Row
              label="Province"
              edit={editing}
              value={shown.province}
              onChange={(v) => set({ province: v })}
            />
            <Row
              label="Phone"
              edit={editing}
              value={shown.phone}
              onChange={(v) => set({ phone: v })}
              mono
            />
            <Row
              label="Email"
              edit={editing}
              value={shown.email}
              onChange={(v) => set({ email: v })}
              mono
            />
            <Row
              label="Website"
              edit={editing}
              value={shown.website}
              onChange={(v) => set({ website: v })}
              mono
            />
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader title="Books" subtitle="Policy — locked once the book has been used" />
          <CardBody className="flex flex-col gap-2.5">
            {editing ? (
              <>
                <Field
                  label="Base currency"
                  hint={company.hasPostings ? 'Fixed — vouchers have posted' : undefined}
                >
                  <Select
                    value={shown.baseCurrency}
                    disabled={company.hasPostings}
                    onChange={(e) => set({ baseCurrency: e.target.value })}
                  >
                    {(refsRes.data?.currencies ?? []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} · {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label="Valuation method"
                  hint={company.hasStockMovement ? 'Fixed — stock has moved' : undefined}
                >
                  <Select
                    value={shown.valuationMethod}
                    disabled={company.hasStockMovement}
                    onChange={(e) => set({ valuationMethod: e.target.value })}
                  >
                    <option value="WEIGHTED_AVERAGE">Weighted average</option>
                    <option value="FIFO">FIFO</option>
                  </Select>
                </Field>
                <Field label="Default batch pick">
                  <Select
                    value={shown.defaultBatchPick}
                    onChange={(e) => set({ defaultBatchPick: e.target.value })}
                  >
                    <option value="FEFO">FEFO — first to expire</option>
                    <option value="FIFO">FIFO — first in</option>
                    <option value="MANUAL">Manual</option>
                  </Select>
                </Field>
                {/*
                  The unit a blank document line opens on. It used to be three
                  literals in three entry screens — MT on the import
                  consignment, BAG on the local purchase and the sales invoice —
                  so a book that trades in something else was wrong on every
                  line of every document.

                  It is a presentation default and nothing more: the unit that
                  reaches the ledger is the one on the line, and a line whose
                  item is chosen takes that product's own stock unit in
                  preference to this. Leaving it blank is a real answer, and on
                  a mixed catalogue it is the better one.
                */}
                <Field
                  label="Default entry unit"
                  hint="What a blank line opens on. A chosen item's own stock unit still wins."
                >
                  <Select
                    value={shown.defaultUomCode ?? ''}
                    onChange={(e) => set({ defaultUomCode: e.target.value })}
                  >
                    <option value="">Each product’s own stock unit</option>
                    {allUoms().map((u) => (
                      <option key={u.code} value={u.code}>
                        {u.code} · {u.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Checkbox
                  checked={shown.allowNegativeStock}
                  onChange={(e) => set({ allowNegativeStock: e.target.checked })}
                  label="Allow negative stock"
                />
                <Checkbox
                  checked={shown.enforceCreditLimit}
                  onChange={(e) => set({ enforceCreditLimit: e.target.checked })}
                  label="Enforce customer credit limits"
                />
                <Checkbox
                  checked={shown.makerCheckerEnabled}
                  onChange={(e) => set({ makerCheckerEnabled: e.target.checked })}
                  label="Maker–checker on approvals"
                />
                <Checkbox
                  checked={shown.batchTracking}
                  onChange={(e) => set({ batchTracking: e.target.checked })}
                  label="Batch and expiry tracking"
                />
                <Field label="Report footer">
                  <Textarea
                    rows={3}
                    value={shown.reportFooter ?? ''}
                    onChange={(e) => set({ reportFooter: e.target.value })}
                  />
                </Field>
              </>
            ) : (
              <>
                <Row label="Base currency" value={shown.baseCurrency} mono />
                <Row
                  label="Valuation method"
                  value={shown.valuationMethod.replace('_', ' ').toLowerCase()}
                />
                <Row
                  label="Financial year starts"
                  value={`${shown.fyStartDay}/${shown.fyStartMonth}`}
                  mono
                />
                <Row label="Timezone" value={shown.timezone} mono />
                <Row label="Batch pick strategy" value={shown.defaultBatchPick} />
                <Row
                  label="Default entry unit"
                  value={shown.defaultUomCode ?? 'each product’s own'}
                  mono={Boolean(shown.defaultUomCode)}
                />
                <Row
                  label="Negative stock"
                  value={shown.allowNegativeStock ? 'Allowed' : 'Refused'}
                />
                <Row
                  label="Credit limits"
                  value={shown.enforceCreditLimit ? 'Enforced' : 'Advisory'}
                />
                <Row label="Maker–checker" value={shown.makerCheckerEnabled ? 'On' : 'Off'} />
                <Row label="Batch tracking" value={shown.batchTracking ? 'On' : 'Off'} />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {shown.reportFooter && !editing && (
        <p className="pt-3.5 text-[11.5px] text-[var(--ink-3)]">{shown.reportFooter}</p>
      )}
    </>
  );
}

// ── Financial year and periods ───────────────────────────────────────────────

function PeriodsScreen() {
  const res = useResource<{
    years: FiscalYearRow[];
    periods: PeriodRow[];
    canEdit: boolean;
    canClose: boolean;
  }>('/setup/calendar', auth());
  const { busy, notice, problem, run } = useAction(res.reload);

  const years = useMemo(() => res.data?.years ?? [], [res.data]);
  const periods = useMemo(() => res.data?.periods ?? [], [res.data]);
  const [yearCode, setYearCode] = useState<string | null>(null);
  /** A reopen needs a reason, so it needs somewhere to type one. */
  const [reopening, setReopening] = useState<{ period: PeriodRow; reason: string } | null>(null);

  const year = years.find((y) => y.code === yearCode) ?? years[0];
  const shown = periods.filter((p) => p.fiscalYearCode === year?.code);

  const move = (period: PeriodRow, status: string, reason?: string) =>
    void run(async () => {
      const result = await apiSend<AdminWriteResult>(
        'POST',
        `/setup/periods/${period.id}/status`,
        { status, reason },
        auth(),
      );
      setReopening(null);
      return result;
    });

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Financial Year & Periods"
        title={year ? year.name : 'Financial year'}
        description="Open and close the financial years you can post entries into."
      />

      <Messages notice={notice} problem={problem} />

      <Tiles>
        <Card>
          <StatTile
            label="Open periods"
            value={String(shown.filter((p) => p.status === 'OPEN').length)}
            hint="posting allowed"
          />
        </Card>
        <Card>
          <StatTile
            label="Closed"
            value={String(shown.filter((p) => p.status === 'CLOSED').length)}
            hint="nothing may post"
          />
        </Card>
        <Card>
          <StatTile
            label="Drafts in closed periods"
            value={String(
              shown.filter((p) => p.status === 'CLOSED').reduce((n, p) => n + p.draftCount, 0),
            )}
            hint="allowed to sit, refused to post"
          />
        </Card>
        <Card>
          <StatTile
            label="Vouchers in this year"
            value={String(year?.voucherCount ?? 0)}
            hint="posted and draft"
          />
        </Card>
      </Tiles>

      {reopening && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={`Reopen ${reopening.period.name}`}
            subtitle="The reason is written to the period and shown beside it from then on"
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setReopening(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={reopening.reason.trim() === ''}
                  onClick={() => move(reopening.period, 'OPEN', reopening.reason)}
                >
                  Reopen
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="flex flex-col gap-3">
            <Field label="Why is this period being reopened" required>
              <Textarea
                rows={2}
                value={reopening.reason}
                onChange={(e) => setReopening({ ...reopening, reason: e.target.value })}
              />
            </Field>
            <Callout tone="warn" title="A closed month that reopens has to say why">
              {reopening.period.postedCount} voucher
              {reopening.period.postedCount === 1 ? '' : 's'} already posted in it. Whoever signed
              off the trial balance for this month took a figure away with them, and this is the
              record of why it may no longer match.
            </Callout>
          </CardBody>
        </Card>
      )}

      <Card flush data-rpt="1">
        <CardHeader
          title="Posting calendar"
          subtitle="One period per year, plus the year-end adjustment window. resolve_period() maps a voucher date here; the status is what gates the posting"
          actions={
            <Toolbar>
              <Select value={year?.code ?? ''} onChange={(e) => setYearCode(e.target.value)}>
                {years.map((y) => (
                  <option key={y.code} value={y.code}>
                    {y.name} · {y.status}
                  </option>
                ))}
              </Select>
              {/*
                No form. The year that follows the last one, starting on the
                company's own fiscal start day, is the only year anybody ever
                opens — so the server derives it and this is one button. Typing
                a code and a date was two chances to open a year that does not
                line up with the one before it.
              */}
              <Button
                size="sm"
                variant="primary"
                disabled={!res.data?.canEdit}
                loading={busy}
                onClick={() =>
                  void run(() =>
                    apiSend<AdminWriteResult>('POST', '/setup/fiscal-years', {}, auth()),
                  )
                }
              >
                Open the next year
              </Button>
            </Toolbar>
          }
        />
        {res.loading ? (
          <SkeletonRows rows={8} />
        ) : res.error ? (
          <CardBody>
            <Callout tone="danger" title="That did not load">
              {res.error}
            </Callout>
          </CardBody>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th width="70px">No.</Th>
                  <Th>Period</Th>
                  <Th width="130px">Starts</Th>
                  <Th width="130px">Ends</Th>
                  <Th align="right" width="110px">
                    Postings
                  </Th>
                  <Th align="right" width="120px">
                    Status
                  </Th>
                  <Th align="right" width="180px">
                    Move to
                  </Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} className="bb-row-h border-b border-[var(--line-2)]">
                    <td className="bb-num px-[var(--pad)] text-[12.5px]">{p.periodNo}</td>
                    <td className="px-2.5 text-[13px]">
                      {p.name}
                      {p.reopenReason && (
                        <span
                          title={`Reopened${p.reopenedByName ? ` by ${p.reopenedByName}` : ''}: ${p.reopenReason}`}
                          className="pl-2 text-[11px] text-[var(--debit)]"
                        >
                          reopened
                        </span>
                      )}
                    </td>
                    <td className="bb-num px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {p.startDate}
                    </td>
                    <td className="bb-num px-2.5 text-[12.5px] text-[var(--ink-2)]">{p.endDate}</td>
                    <td className="bb-num px-2.5 text-right text-[12.5px]">
                      {p.postedCount}
                      {p.draftCount > 0 && (
                        <span className="pl-1 text-[11px] text-[var(--ink-3)]">
                          +{p.draftCount} dr
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 text-right">
                      <Badge
                        tone={
                          p.status === 'OPEN'
                            ? 'ok'
                            : p.status === 'FUTURE'
                              ? 'mute'
                              : p.status === 'SOFT_CLOSED'
                                ? 'warn'
                                : 'info'
                        }
                      >
                        {p.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-[var(--pad)] text-right" data-noprint="1">
                      <Toolbar>
                        {p.status !== 'OPEN' && (
                          <Button
                            size="sm"
                            disabled={!res.data?.canEdit}
                            loading={busy}
                            onClick={() =>
                              p.status === 'CLOSED'
                                ? setReopening({ period: p, reason: '' })
                                : move(p, 'OPEN')
                            }
                          >
                            Open
                          </Button>
                        )}
                        {p.status !== 'CLOSED' && (
                          <Button
                            size="sm"
                            disabled={!res.data?.canClose}
                            loading={busy}
                            onClick={() => move(p, 'CLOSED')}
                          >
                            Close
                          </Button>
                        )}
                      </Toolbar>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ── Voucher numbering ────────────────────────────────────────────────────────

interface SeriesDraft {
  mode: 'create' | 'edit';
  id?: string;
  docType: string;
  prefix: string;
  separator: string;
  yearToken: string;
  padding: number;
  nextValue: number;
  minimumNext: number;
  isGapless: boolean;
  isActive: boolean;
  fiscalYearCode: string;
  branchCode: string;
}

function NumberingScreen() {
  const res = useResource<{ series: NumberSeriesRow[]; canEdit: boolean; canCreate: boolean }>(
    '/setup/numbering',
    auth(),
  );
  const refsRes = useResource<SetupRefs>('/setup/refs', auth());
  const series = useMemo(() => res.data?.series ?? [], [res.data]);
  const refs = refsRes.data;
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<SeriesDraft | null>(null);

  const preview = (d: SeriesDraft) =>
    d.prefix +
    d.separator +
    (d.yearToken ? d.yearToken + d.separator : '') +
    String(d.nextValue).padStart(d.padding, '0');

  const save = (d: SeriesDraft) =>
    void run(async () => {
      const payload = {
        docType: d.docType,
        prefix: d.prefix,
        separator: d.separator,
        yearToken: d.yearToken || null,
        padding: d.padding,
        nextValue: d.nextValue,
        isGapless: d.isGapless,
        isActive: d.isActive,
        fiscalYearCode: d.fiscalYearCode || null,
        branchCode: d.branchCode || null,
      };
      const result =
        d.mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/setup/numbering', payload, auth())
          : await apiSend<AdminWriteResult>('PATCH', `/setup/numbering/${d.id}`, payload, auth());
      setDraft(null);
      return result;
    });

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Voucher Numbering"
        title="Numbering series"
        description="Set how each type of document is numbered."
      />

      <Messages notice={notice} problem={problem} />

      {draft && refs && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={draft.mode === 'create' ? 'New series' : `Edit ${draft.docType}`}
            subtitle={`Next document will be ${preview(draft)}`}
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.docType || !draft.prefix || draft.nextValue < draft.minimumNext}
                  onClick={() => save(draft)}
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Document type" required hint="JV, INV, PUR — matches the voucher type">
              <Input
                value={draft.docType}
                disabled={draft.mode === 'edit'}
                onChange={(e) => setDraft({ ...draft, docType: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Prefix" required>
              <Input
                value={draft.prefix}
                onChange={(e) => setDraft({ ...draft, prefix: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Separator">
              <Input
                value={draft.separator}
                onChange={(e) => setDraft({ ...draft, separator: e.target.value })}
              />
            </Field>
            <Field label="Year token" hint="26 — omitted when blank">
              <Input
                value={draft.yearToken}
                onChange={(e) => setDraft({ ...draft, yearToken: e.target.value })}
              />
            </Field>
            <Field label="Padding" hint="3 to 12 digits">
              <Input
                type="number"
                value={String(draft.padding)}
                onChange={(e) => setDraft({ ...draft, padding: Number(e.target.value) })}
              />
            </Field>
            <Field
              label="Next number"
              hint={
                draft.mode === 'edit'
                  ? `Cannot go below ${draft.minimumNext} — those numbers are issued`
                  : 'Start higher to continue a series from another system'
              }
            >
              <Input
                type="number"
                value={String(draft.nextValue)}
                onChange={(e) => setDraft({ ...draft, nextValue: Number(e.target.value) })}
              />
            </Field>
            <Field label="Fiscal year" hint="Blank means every year">
              <Select
                value={draft.fiscalYearCode}
                disabled={draft.mode === 'edit'}
                onChange={(e) => setDraft({ ...draft, fiscalYearCode: e.target.value })}
              >
                <option value="">— any —</option>
                {refs.fiscalYears.map((y) => (
                  <option key={y.code} value={y.code}>
                    {y.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Branch" hint="Blank means every branch">
              <Select
                value={draft.branchCode}
                disabled={draft.mode === 'edit'}
                onChange={(e) => setDraft({ ...draft, branchCode: e.target.value })}
              >
                <option value="">— any —</option>
                {refs.branches.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.code} · {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          </CardBody>
        </Card>
      )}

      <Card flush data-rpt="1">
        <CardHeader
          title="Series"
          subtitle="A document and the voucher it raises share one number — allocating two would mean the paper invoice could not find its entry"
          actions={
            <Button
              size="sm"
              variant="primary"
              disabled={!res.data?.canCreate || !refs}
              onClick={() =>
                setDraft({
                  mode: 'create',
                  docType: '',
                  prefix: '',
                  separator: '-',
                  yearToken: '',
                  padding: 6,
                  nextValue: 1,
                  minimumNext: 1,
                  isGapless: true,
                  isActive: true,
                  fiscalYearCode: '',
                  branchCode: '',
                })
              }
            >
              New series
            </Button>
          }
        />
        {res.loading ? (
          <SkeletonRows rows={8} />
        ) : res.error ? (
          <CardBody>
            <Callout tone="danger" title="That did not load">
              {res.error}
            </Callout>
          </CardBody>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <Th width="100px">Document</Th>
                  <Th width="170px">Next number</Th>
                  <Th align="right" width="110px">
                    Issued
                  </Th>
                  <Th width="120px">Year</Th>
                  <Th width="140px">Branch</Th>
                  <Th width="110px">Rule</Th>
                  <Th align="right" width="150px">
                    &nbsp;
                  </Th>
                </tr>
              </thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.id} className="bb-row-h border-b border-[var(--line-2)]">
                    <td className="px-[var(--pad)] text-[13px] font-medium">{s.docType}</td>
                    <td className="bb-num px-2.5 text-[12.5px]">{s.nextNumber}</td>
                    <td className="bb-num px-2.5 text-right text-[12.5px] text-[var(--ink-2)]">
                      {Number(s.nextValue) - 1}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {s.fiscalYearCode ?? 'Any'}
                    </td>
                    <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
                      {s.branchCode ?? 'Any'}
                    </td>
                    <td className="px-2.5 text-[12px] text-[var(--ink-3)]">
                      {s.isGapless ? 'Gapless' : 'May gap'}
                      {!s.isActive && ' · off'}
                    </td>
                    <td className="px-[var(--pad)] text-right" data-noprint="1">
                      <Button
                        size="sm"
                        disabled={!res.data?.canEdit}
                        onClick={() =>
                          setDraft({
                            mode: 'edit',
                            id: s.id,
                            docType: s.docType,
                            prefix: s.prefix,
                            separator: s.separator,
                            yearToken: s.yearToken ?? '',
                            padding: s.padding,
                            nextValue: Number(s.nextValue),
                            minimumNext: Number(s.nextValue),
                            isGapless: s.isGapless,
                            isActive: s.isActive,
                            fiscalYearCode: s.fiscalYearCode ?? '',
                            branchCode: s.branchCode ?? '',
                          })
                        }
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ── Tax and FX ───────────────────────────────────────────────────────────────

function TaxAndFxScreen() {
  const res = useResource<{
    taxCodes: TaxCodeRow[];
    rates: FxRateRow[];
    policy: { baseCurrency: string };
    kinds: string[];
    computations: string[];
    rateTypes: string[];
    canCreateTax: boolean;
    canEditTax: boolean;
    canDeleteTax: boolean;
    canEditFx: boolean;
  }>('/setup/tax-fx', auth());
  const refsRes = useResource<SetupRefs>('/setup/refs', auth());
  const refs = refsRes.data;
  const { busy, notice, problem, run } = useAction(res.reload);

  const taxCodes = useMemo(() => res.data?.taxCodes ?? [], [res.data]);
  const rates = useMemo(() => res.data?.rates ?? [], [res.data]);

  const [taxDraft, setTaxDraft] = useState<Record<string, string> | null>(null);
  const [rateDraft, setRateDraft] = useState<Record<string, string> | null>(null);

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Tax & FX Settings"
        title="Tax and FX"
        description="The tax rates and exchange rates this book uses."
      />

      <Messages notice={notice} problem={problem} />

      {taxDraft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title="New tax rate"
            subtitle="Adding a rate for a code that already exists closes the previous one the day before this starts"
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setTaxDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!taxDraft.code || !taxDraft.name || !taxDraft.effectiveFrom}
                  onClick={() =>
                    void run(async () => {
                      const result = await apiSend<AdminWriteResult>(
                        'POST',
                        '/setup/tax-codes',
                        taxDraft,
                        auth(),
                      );
                      setTaxDraft(null);
                      return result;
                    })
                  }
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Code" required hint="ST18, WHT-4.5-FILER">
              <Input
                value={taxDraft.code ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required>
              <Input
                value={taxDraft.name ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, name: e.target.value })}
              />
            </Field>
            <Field label="Kind" required>
              <Select
                value={taxDraft.kind ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, kind: e.target.value })}
              >
                <option value="">—</option>
                {(res.data?.kinds ?? []).map((k) => (
                  <option key={k} value={k}>
                    {k.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Computation">
              <Select
                value={taxDraft.computation ?? 'PERCENT'}
                onChange={(e) => setTaxDraft({ ...taxDraft, computation: e.target.value })}
              >
                {(res.data?.computations ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Rate %" hint="Filer rate">
              <Input
                value={taxDraft.rate ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, rate: e.target.value })}
              />
            </Field>
            <Field label="Non-filer rate %" hint="Resolved by party.is_filer">
              <Input
                value={taxDraft.nonFilerRate ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, nonFilerRate: e.target.value })}
              />
            </Field>
            <Field label="Posts to account">
              <Select
                value={taxDraft.accountCode ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, accountCode: e.target.value })}
              >
                <option value="">—</option>
                {(refs?.accounts ?? []).map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} · {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Effective from" required>
              <Input
                type="date"
                value={taxDraft.effectiveFrom ?? today()}
                onChange={(e) => setTaxDraft({ ...taxDraft, effectiveFrom: e.target.value })}
              />
            </Field>
            <Field label="Statutory section" hint="Printed on the FBR register">
              <Input
                value={taxDraft.statutorySection ?? ''}
                onChange={(e) => setTaxDraft({ ...taxDraft, statutorySection: e.target.value })}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      {rateDraft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title="Record an FX rate"
            subtitle="One rate per day per pair per type. Saving over today's rate is a correction; a posted voucher keeps its own."
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setRateDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!rateDraft.rate || !rateDraft.rateDate}
                  onClick={() =>
                    void run(async () => {
                      const result = await apiSend<AdminWriteResult>(
                        'POST',
                        '/setup/fx-rates',
                        rateDraft,
                        auth(),
                      );
                      setRateDraft(null);
                      return result;
                    })
                  }
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <Field label="Date" required>
              <Input
                type="date"
                value={rateDraft.rateDate ?? today()}
                onChange={(e) => setRateDraft({ ...rateDraft, rateDate: e.target.value })}
              />
            </Field>
            <Field label="From" required>
              <Select
                value={rateDraft.fromCurrency ?? ''}
                onChange={(e) => setRateDraft({ ...rateDraft, fromCurrency: e.target.value })}
              >
                {(refs?.currencies ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="To" required>
              <Select
                value={rateDraft.toCurrency ?? ''}
                onChange={(e) => setRateDraft({ ...rateDraft, toCurrency: e.target.value })}
              >
                {(refs?.currencies ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Type" hint="CUSTOMS is the BoE rate — deliberately not SPOT">
              <Select
                value={rateDraft.rateType ?? 'SPOT'}
                onChange={(e) => setRateDraft({ ...rateDraft, rateType: e.target.value })}
              >
                {(res.data?.rateTypes ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Rate" required hint="base = txn × rate">
              <Input
                value={rateDraft.rate ?? ''}
                onChange={(e) => setRateDraft({ ...rateDraft, rate: e.target.value })}
              />
            </Field>
            <Field label="Source" hint="SBP, Meezan TT, manual">
              <Input
                value={rateDraft.source ?? ''}
                onChange={(e) => setRateDraft({ ...rateDraft, source: e.target.value })}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      <div className="grid gap-3.5 xl:grid-cols-2">
        <Card flush>
          <CardHeader
            title="Tax codes"
            subtitle="FBR-ready registers derive from these"
            actions={
              <Button
                size="sm"
                variant="primary"
                disabled={!res.data?.canCreateTax}
                onClick={() =>
                  setTaxDraft({ effectiveFrom: today(), computation: 'PERCENT', rate: '0' })
                }
              >
                New rate
              </Button>
            }
          />
          {res.loading ? (
            <SkeletonRows rows={6} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th>Code</Th>
                    <Th align="right" width="80px">
                      Rate
                    </Th>
                    <Th width="130px">Effective</Th>
                    <Th align="right" width="100px">
                      On docs
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {taxCodes.map((t) => (
                    <tr key={t.id} className="bb-row-h border-b border-[var(--line-2)]">
                      <td className="px-[var(--pad)] text-[12.5px]">
                        <span className="bb-mono pr-2 font-medium">{t.code}</span>
                        <span className="text-[var(--ink-2)]">{t.name}</span>
                      </td>
                      <td className="bb-num px-2.5 text-right text-[12.5px]">
                        {t.computation === 'PERCENT'
                          ? `${t.rate}%`
                          : t.computation.replace('_', ' ')}
                      </td>
                      <td className="bb-num px-2.5 text-[12px] text-[var(--ink-2)]">
                        {t.effectiveFrom}
                        {t.effectiveTo && ` → ${t.effectiveTo}`}
                      </td>
                      <td className="bb-num px-[var(--pad)] text-right text-[12px] text-[var(--ink-3)]">
                        {t.documentCount > 0 ? `${t.documentCount} · frozen` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card flush>
          <CardHeader
            title="FX rates"
            subtitle={`Everything converts into ${res.data?.policy.baseCurrency ?? 'PKR'}`}
            actions={
              <Button
                size="sm"
                variant="primary"
                disabled={!res.data?.canEditFx}
                onClick={() =>
                  setRateDraft({
                    rateDate: today(),
                    fromCurrency: 'USD',
                    toCurrency: res.data?.policy.baseCurrency ?? 'PKR',
                    rateType: 'SPOT',
                  })
                }
              >
                Record rate
              </Button>
            }
          />
          {res.loading ? (
            <SkeletonRows rows={6} />
          ) : rates.length === 0 ? (
            <EmptyState title="No rates yet" description="A USD voucher cannot post without one." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-2)]">
                    <Th width="120px">Date</Th>
                    <Th width="110px">Pair</Th>
                    <Th width="100px">Type</Th>
                    <Th align="right" width="130px">
                      Rate
                    </Th>
                    <Th width="120px">Source</Th>
                  </tr>
                </thead>
                <tbody>
                  {rates.slice(0, 30).map((r) => (
                    <tr key={r.id} className="bb-row-h border-b border-[var(--line-2)]">
                      <td className="bb-num px-[var(--pad)] text-[12.5px]">{r.rateDate}</td>
                      <td className="bb-mono px-2.5 text-[12.5px]">
                        {r.fromCurrency}/{r.toCurrency}
                      </td>
                      <td className="px-2.5 text-[12px] text-[var(--ink-2)]">{r.rateType}</td>
                      <td className="bb-num px-2.5 text-right text-[12.5px] font-medium">
                        {r.rate}
                      </td>
                      <td className="px-[var(--pad)] text-[12px] text-[var(--ink-3)]">
                        {r.source ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

// ── Branches ─────────────────────────────────────────────────────────────────

function BranchesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const res = useResource<{
    branches: BranchRow[];
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  }>('/setup/branches', { ...auth(), include: includeInactive ? 'inactive' : undefined });
  const branches = useMemo(() => res.data?.branches ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Record<string, string | boolean> | null>(null);

  const save = () => {
    if (!draft) return;
    void run(async () => {
      const creating = draft.mode === 'create';
      const payload = {
        code: draft.code,
        name: draft.name,
        addressLine1: draft.addressLine1 || null,
        city: draft.city || null,
        phone: draft.phone || null,
        isHeadOffice: Boolean(draft.isHeadOffice),
        isActive: Boolean(draft.isActive),
      };
      const result = creating
        ? await apiSend<AdminWriteResult>('POST', '/setup/branches', payload, auth())
        : await apiSend<AdminWriteResult>(
            'PATCH',
            `/setup/branches/${encodeURIComponent(String(draft.code))}`,
            { ...payload, code: undefined },
            auth(),
          );
      setDraft(null);
      return result;
    });
  };

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Branches"
        title="Branches"
        description="The offices documents can be raised from, one of them the head office."
      />

      <Messages notice={notice} problem={problem} />

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={draft.mode === 'create' ? 'New branch' : `Edit ${draft.code}`}
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.name || !draft.code}
                  onClick={save}
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Code" required hint="HO, LHR — appears on every reference">
              <Input
                value={String(draft.code ?? '')}
                disabled={draft.mode === 'edit'}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required hint="The place, not the words Head Office">
              <Input
                value={String(draft.name ?? '')}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label="City">
              <Input
                value={String(draft.city ?? '')}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <Input
                value={String(draft.phone ?? '')}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
            <Field label="Address">
              <Input
                value={String(draft.addressLine1 ?? '')}
                onChange={(e) => setDraft({ ...draft, addressLine1: e.target.value })}
              />
            </Field>
            <Field label="Head office">
              <Checkbox
                checked={Boolean(draft.isHeadOffice)}
                onChange={(e) => setDraft({ ...draft, isHeadOffice: e.target.checked })}
                label="Promoting this demotes the current head office"
              />
            </Field>
            {draft.mode === 'edit' && (
              <Field label="Active">
                <Checkbox
                  checked={Boolean(draft.isActive)}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  label="Offered in the top bar"
                />
              </Field>
            )}
          </CardBody>
        </Card>
      )}

      <MasterTable
        title="Branches"
        subtitle="Counts are what a delete would have to leave behind"
        loading={res.loading}
        error={res.error}
        empty="No branches"
        onNew={
          res.data?.canCreate
            ? () => setDraft({ mode: 'create', isActive: true, isHeadOffice: false })
            : undefined
        }
        newLabel="New branch"
        toggle={{
          on: includeInactive,
          onToggle: () => setIncludeInactive((v) => !v),
          label: 'Show inactive',
        }}
        columns={['Code', 'Name', 'City', 'Users', 'Warehouses', 'Vouchers', '']}
      >
        {branches.map((b) => (
          <tr key={b.id} className="bb-row-h border-b border-[var(--line-2)]">
            <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">
              {b.code}
              {b.isHeadOffice && (
                <Badge tone="info" className="ml-2">
                  HO
                </Badge>
              )}
            </td>
            <td className="px-2.5 text-[13px]">
              {b.name}
              {!b.isActive && (
                <Badge tone="mute" className="ml-2">
                  Inactive
                </Badge>
              )}
            </td>
            <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">{b.city ?? '—'}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{b.userCount}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{b.warehouseCount}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{b.voucherCount}</td>
            <td className="px-[var(--pad)] text-right" data-noprint="1">
              <Toolbar>
                <Button
                  size="sm"
                  disabled={!res.data?.canEdit}
                  onClick={() =>
                    setDraft({
                      mode: 'edit',
                      code: b.code,
                      name: b.name,
                      city: b.city ?? '',
                      phone: b.phone ?? '',
                      addressLine1: b.addressLine1 ?? '',
                      isHeadOffice: b.isHeadOffice,
                      isActive: b.isActive,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={!res.data?.canDelete || b.isHeadOffice}
                  onClick={() =>
                    void run(() =>
                      apiSend<AdminWriteResult>(
                        'DELETE',
                        `/setup/branches/${encodeURIComponent(b.code)}`,
                        undefined,
                        auth(),
                      ),
                    )
                  }
                >
                  Delete
                </Button>
              </Toolbar>
            </td>
          </tr>
        ))}
      </MasterTable>
    </>
  );
}

// ── Currencies ───────────────────────────────────────────────────────────────

function CurrenciesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const res = useResource<{
    currencies: CurrencyRow[];
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  }>('/setup/currencies', { ...auth(), include: includeInactive ? 'inactive' : undefined });
  const currencies = useMemo(() => res.data?.currencies ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Record<string, string | boolean | number> | null>(null);

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Currencies"
        title="Currencies"
        description="The currencies this book is allowed to trade in."
      />

      <Messages notice={notice} problem={problem} />

      <Callout tone="warn" title="Adding a currency is not fully reversible">
        PostgreSQL can add a value to an enum and cannot remove one. Deleting a currency here
        removes it from this register — every picker in the application reads the register, so the
        code stops being offered and stops being selectable — but the label itself stays in the
        database type for ever, unreferenced. Adding the same currency back later reuses it. Nothing
        breaks either way; it is simply worth knowing before adding one to try it out.
      </Callout>

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={draft.mode === 'create' ? 'New currency' : `Edit ${draft.code}`}
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.code || !draft.name || String(draft.code).length !== 3}
                  onClick={() =>
                    void run(async () => {
                      const payload = {
                        code: draft.code,
                        name: draft.name,
                        symbol: draft.symbol || null,
                        decimals: Number(draft.decimals ?? 2),
                        isActive: Boolean(draft.isActive),
                      };
                      const result =
                        draft.mode === 'create'
                          ? await apiSend<AdminWriteResult>(
                              'POST',
                              '/setup/currencies',
                              payload,
                              auth(),
                            )
                          : await apiSend<AdminWriteResult>(
                              'PATCH',
                              `/setup/currencies/${String(draft.code)}`,
                              { ...payload, code: undefined },
                              auth(),
                            );
                      setDraft(null);
                      return result;
                    })
                  }
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Code" required hint="ISO 4217 — three letters, like AED">
              <Input
                value={String(draft.code ?? '')}
                disabled={draft.mode === 'edit'}
                maxLength={3}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required>
              <Input
                value={String(draft.name ?? '')}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label="Symbol" hint="Rs, $, د.إ">
              <Input
                value={String(draft.symbol ?? '')}
                onChange={(e) => setDraft({ ...draft, symbol: e.target.value })}
              />
            </Field>
            <Field label="Decimals" hint="Presentation only — storage is always 4 places">
              <Input
                type="number"
                value={String(draft.decimals ?? 2)}
                onChange={(e) => setDraft({ ...draft, decimals: Number(e.target.value) })}
              />
            </Field>
            {draft.mode === 'edit' && (
              <Field label="Active">
                <Checkbox
                  checked={Boolean(draft.isActive)}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  label="Offered on vouchers and accounts"
                />
              </Field>
            )}
          </CardBody>
        </Card>
      )}

      <MasterTable
        title="Currency register"
        subtitle="The base currency is the one every balance is expressed in and cannot be switched off"
        loading={res.loading}
        error={res.error}
        empty="No currencies"
        onNew={
          res.data?.canCreate
            ? () => setDraft({ mode: 'create', decimals: 2, isActive: true })
            : undefined
        }
        newLabel="New currency"
        toggle={{
          on: includeInactive,
          onToggle: () => setIncludeInactive((v) => !v),
          label: 'Show inactive',
        }}
        columns={['Code', 'Name', 'Symbol', 'Latest rate', 'Vouchers', 'Accounts', '']}
      >
        {currencies.map((c) => (
          <tr key={c.id} className="bb-row-h border-b border-[var(--line-2)]">
            <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">
              {c.code}
              {c.isBase && (
                <Badge tone="ok" className="ml-2">
                  Base
                </Badge>
              )}
            </td>
            <td className="px-2.5 text-[13px]">
              {c.name}
              {!c.isActive && (
                <Badge tone="mute" className="ml-2">
                  Inactive
                </Badge>
              )}
            </td>
            <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">{c.symbol ?? '—'}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">
              {c.isBase
                ? '—'
                : (c.latestRate ?? <span className="text-[var(--debit)]">no rate</span>)}
            </td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{c.voucherCount}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{c.accountCount}</td>
            <td className="px-[var(--pad)] text-right" data-noprint="1">
              <Toolbar>
                <Button
                  size="sm"
                  disabled={!res.data?.canEdit}
                  onClick={() =>
                    setDraft({
                      mode: 'edit',
                      code: c.code,
                      name: c.name,
                      symbol: c.symbol ?? '',
                      decimals: c.decimals,
                      isActive: c.isActive,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={!res.data?.canDelete || c.isBase}
                  onClick={() =>
                    void run(() =>
                      apiSend<AdminWriteResult>(
                        'DELETE',
                        `/setup/currencies/${c.code}`,
                        undefined,
                        auth(),
                      ),
                    )
                  }
                >
                  Delete
                </Button>
              </Toolbar>
            </td>
          </tr>
        ))}
      </MasterTable>
    </>
  );
}

// ── Units of measure ─────────────────────────────────────────────────────────

function UomsScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const res = useResource<{
    uoms: UomRow[];
    dimensions: string[];
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  }>('/setup/uoms', { ...auth(), include: includeInactive ? 'inactive' : undefined });
  const uoms = useMemo(() => res.data?.uoms ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Record<string, string | boolean | number> | null>(null);

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Units of Measure"
        title="Units of measure"
        description="The units your products are measured and traded in."
      />

      <Messages notice={notice} problem={problem} />

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={draft.mode === 'create' ? 'New unit' : `Edit ${draft.code}`}
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.code || !draft.name || !draft.dimension}
                  onClick={() =>
                    void run(async () => {
                      const payload = {
                        code: draft.code,
                        name: draft.name,
                        dimension: draft.dimension,
                        decimals: Number(draft.decimals ?? 3),
                        factorToBase: String(draft.factorToBase ?? '1'),
                        isActive: Boolean(draft.isActive),
                      };
                      const result =
                        draft.mode === 'create'
                          ? await apiSend<AdminWriteResult>('POST', '/setup/uoms', payload, auth())
                          : await apiSend<AdminWriteResult>(
                              'PATCH',
                              `/setup/uoms/${encodeURIComponent(String(draft.code))}`,
                              { ...payload, code: undefined },
                              auth(),
                            );
                      setDraft(null);
                      return result;
                    })
                  }
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Code" required hint="KG, LTR, G, ML, MT">
              <Input
                value={String(draft.code ?? '')}
                disabled={draft.mode === 'edit'}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required>
              <Input
                value={String(draft.name ?? '')}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field
              label="Dimension"
              required
              hint="What it measures — fixed once stock has moved in it"
            >
              <Select
                value={String(draft.dimension ?? '')}
                onChange={(e) => setDraft({ ...draft, dimension: e.target.value })}
              >
                <option value="">—</option>
                {(res.data?.dimensions ?? []).map((d) => (
                  <option key={d} value={d}>
                    {d[0]}
                    {d.slice(1).toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Decimals"
              hint="A piece is whole: 0. A kilogram is weighed to the gram: 3."
            >
              <Input
                type="number"
                value={String(draft.decimals ?? 3)}
                onChange={(e) => setDraft({ ...draft, decimals: Number(e.target.value) })}
              />
            </Field>
            {/* The field that makes a unit work rather than merely exist. Every
                product's conversion is this number divided by its own unit's, so
                filling it in is the entire mapping — there is nothing to set per
                product afterwards. */}
            <Field
              label="Size"
              required
              hint="How many of the dimension's base unit one of this is. Kilogram and Litre are 1; a Metric Ton is 1000; a millilitre is 0.001."
            >
              <Input
                value={String(draft.factorToBase ?? '1')}
                onChange={(e) => setDraft({ ...draft, factorToBase: e.target.value })}
              />
            </Field>
            {draft.mode === 'edit' && (
              <Field label="Active">
                <Checkbox
                  checked={Boolean(draft.isActive)}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  label="Offered on product and document lines"
                />
              </Field>
            )}
          </CardBody>
        </Card>
      )}

      <MasterTable
        title="Units"
        subtitle="A unit a product is based in cannot change dimension or be switched off"
        loading={res.loading}
        error={res.error}
        empty="No units"
        onNew={
          res.data?.canCreate
            ? () =>
                setDraft({
                  mode: 'create',
                  decimals: 3,
                  factorToBase: '1',
                  isActive: true,
                  dimension: 'WEIGHT',
                })
            : undefined
        }
        newLabel="New unit"
        toggle={{
          on: includeInactive,
          onToggle: () => setIncludeInactive((v) => !v),
          label: 'Show inactive',
        }}
        columns={['Code', 'Name', 'Dimension', 'Size', 'Decimals', 'Base for', 'Movements', '']}
      >
        {uoms.map((u) => (
          <tr key={u.id} className="bb-row-h border-b border-[var(--line-2)]">
            <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">{u.code}</td>
            <td className="px-2.5 text-[13px]">
              {u.name}
              {!u.isActive && (
                <Badge tone="mute" className="ml-2">
                  Inactive
                </Badge>
              )}
            </td>
            <td className="px-2.5 text-[12.5px] text-[var(--ink-2)]">
              {u.dimension[0]}
              {u.dimension.slice(1).toLowerCase()}
            </td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">
              {String(Number(u.factorToBase))}
            </td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{u.decimals}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">
              {u.baseForProducts > 0
                ? `${u.baseForProducts} product${u.baseForProducts === 1 ? '' : 's'}`
                : '—'}
            </td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{u.movementCount}</td>
            <td className="px-[var(--pad)] text-right" data-noprint="1">
              <Toolbar>
                <Button
                  size="sm"
                  disabled={!res.data?.canEdit}
                  onClick={() =>
                    setDraft({
                      mode: 'edit',
                      code: u.code,
                      name: u.name,
                      dimension: u.dimension,
                      decimals: u.decimals,
                      factorToBase: u.factorToBase,
                      isActive: u.isActive,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={!res.data?.canDelete || u.baseForProducts > 0 || u.movementCount > 0}
                  onClick={() =>
                    void run(() =>
                      apiSend<AdminWriteResult>(
                        'DELETE',
                        `/setup/uoms/${encodeURIComponent(u.code)}`,
                        undefined,
                        auth(),
                      ),
                    )
                  }
                >
                  Delete
                </Button>
              </Toolbar>
            </td>
          </tr>
        ))}
      </MasterTable>
    </>
  );
}

// ── Shared presentation ──────────────────────────────────────────────────────

/** A label/value pair that becomes an input when the card is in edit mode. */
function Row({
  label,
  value,
  mono,
  edit,
  onChange,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  edit?: boolean;
  onChange?: (value: string) => void;
}) {
  if (edit && onChange) {
    return (
      <Field label={label}>
        <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </Field>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4 text-[12.5px]">
      <span className="text-[var(--ink-2)]">{label}</span>
      <span className={mono ? 'bb-num text-right font-medium' : 'text-right font-medium'}>
        {value ?? '—'}
      </span>
    </div>
  );
}

// ── Link accounts ────────────────────────────────────────────────────────────

/**
 * Setup → Link Accounts: which account each module posts to.
 *
 * The page answers one question — "a sales invoice puts its tax where?" — that
 * was previously answerable only by reading `sales.ts` and then querying
 * `accounts.system_key` by hand. Every row is a key that posting code resolves,
 * grouped by the module whose screens produce the entry, with the sentence
 * describing what lands there beside it.
 *
 * Three things about the shape, each of which is the point rather than styling:
 *
 * · **The picker is filtered to the account types the link allows.** Pointing
 *   Sales at an expense account is the mistake this screen could most easily
 *   introduce, and nothing downstream would refuse it — the voucher still
 *   balances and the trial balance still sums, and it surfaces as a gross margin
 *   nobody can explain. The server checks it as well; this is what stops it being
 *   offered.
 *
 * · **A control link offers group heads and every other link does not.** A
 *   control account is *meant* to be a head — its level-5 detail takes the line —
 *   so the `isLeaf` filter that is right everywhere else would empty the list for
 *   Accounts Receivable.
 *
 * · **A missing link is a callout, not a blank cell.** A key the chart carries no
 *   account for is a 500 on the next document of that kind, which is worth being
 *   told before it happens rather than a dash to notice.
 */
function LinkAccountsScreen() {
  const res = useResource<AccountLinks>('/setup/account-links', auth());
  const chart = useResource<{ accounts: ApiAccount[] }>('/accounts', auth());
  const { busy, notice, problem, run } = useAction(res.reload);
  /** The key being re-pointed. One at a time: each write moves one link. */
  const [editing, setEditing] = useState<string | null>(null);

  const accounts = useMemo(() => chart.data?.accounts ?? [], [chart.data]);

  if (res.loading) return <SkeletonRows rows={10} />;
  if (res.error) {
    return (
      <Callout tone="danger" title="That did not load">
        {res.error}
      </Callout>
    );
  }
  const data = res.data;
  if (!data) return <EmptyState title="No links" description="Run pnpm db:seed." />;

  return (
    <>
      <PageHeader
        breadcrumb="Setup / Link Accounts"
        title="Link accounts"
        description="Choose which account each part of the system posts to."
      />

      <Messages notice={notice} problem={problem} />

      {data.missing.length > 0 && (
        <Callout tone="danger" title="The chart carries no account for these links">
          {data.missing.join(', ')} — the next document that needs one of them is refused, naming
          it. Point each at an account below, or re-run the seed.
        </Callout>
      )}

      {data.groups.map((group) => (
        <Card flush className="mb-3.5" key={group.module} data-rpt="1">
          <CardHeader
            title={group.label}
            subtitle={`${group.links.length} link${group.links.length === 1 ? '' : 's'}`}
          />
          {group.note && (
            <CardBody className="border-b border-[var(--line)] text-[12px] text-[var(--ink-2)]">
              {group.note}
            </CardBody>
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr>
                  <Th>What posts</Th>
                  <Th>Side</Th>
                  <Th>Account</Th>
                  <Th align="right">Change</Th>
                </tr>
              </thead>
              <tbody>
                {group.links.map((link) => (
                  <tr key={link.key} className="border-t border-[var(--line)] align-top">
                    <td className="bb-pad">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-medium text-[var(--ink)]">{link.label}</span>
                        {/*
                          Every row on this page looks like it decides where money
                          goes, and some of them decide nothing — a fallback the
                          document usually overrides, or a key nothing resolves at
                          all. Badging the two makes the page honest without
                          hiding them: an accountant looking for withholding on a
                          supplier payment should read "nowhere yet" rather than
                          re-point an account and wait for an entry that never
                          comes. `POSTS` gets no badge, being the common case.
                        */}
                        {link.use === 'DEFAULT' && <Badge tone="info">Default</Badge>}
                        {link.use === 'UNUSED' && <Badge tone="warn">Nothing posts here</Badge>}
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-[var(--ink-2)]">{link.posts}</div>
                      <div className="bb-mono mt-0.5 text-[10.5px] text-[var(--ink-3)]">
                        {link.key}
                      </div>
                    </td>
                    <td className="bb-pad whitespace-nowrap">
                      {/* Credit reads jade and debit copper through the tokens
                          `ok` and `bad` carry; a debit is not an error, and the
                          pair survives colour blindness where red/green does
                          not. `Dr/Cr` — a link that goes both ways — is neither. */}
                      <Badge tone={link.side === 'Cr' ? 'ok' : link.side === 'Dr' ? 'bad' : 'info'}>
                        {link.side}
                      </Badge>
                    </td>
                    <td className="bb-pad">
                      {editing === link.key ? (
                        <LinkPicker
                          link={link}
                          accounts={accounts}
                          busy={busy}
                          onCancel={() => setEditing(null)}
                          onChoose={(code) =>
                            void run(async () => {
                              const result = await apiSend<AccountLinkWriteResult>(
                                'PATCH',
                                `/setup/account-links/${encodeURIComponent(link.key)}`,
                                { accountCode: code },
                                auth(),
                              );
                              setEditing(null);
                              return result;
                            })
                          }
                        />
                      ) : link.code ? (
                        <>
                          <span className="bb-mono">{link.code}</span> {link.name}
                          <div className="mt-0.5 flex flex-wrap gap-1.5">
                            <Badge tone="info">Level {link.level}</Badge>
                            {link.isControl && <Badge tone="info">Control head</Badge>}
                            {link.unpostable && (
                              <Badge tone="bad">
                                {link.isActive === false ? 'Deactivated' : 'Group head'}
                              </Badge>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-[var(--debit)]">Not linked</span>
                      )}
                    </td>
                    <td className="bb-pad text-right">
                      {editing !== link.key && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(link.key)}>
                          Re-point
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </>
  );
}

/**
 * The account picker for one link, narrowed to what will actually post.
 *
 * `accountType` comes from the server, so the screen carries no second copy of
 * which link may be which type — the two could then disagree, and the one on
 * screen would be the one people trusted.
 */
function LinkPicker({
  link,
  accounts,
  busy,
  onChoose,
  onCancel,
}: {
  link: AccountLinkRow;
  accounts: readonly ApiAccount[];
  busy: boolean;
  onChoose: (code: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(link.code ?? '');

  const options = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            a.isActive &&
            link.accountType.includes(a.type) &&
            // A control link opens its sub-accounts underneath, so it wants a
            // head with room below it; every other link wants a leaf.
            (link.isControl ? a.level < 5 : a.isLeaf),
        )
        .map((a) => ({
          value: a.code,
          label: `${a.code} · ${a.name}`,
          hint: a.isLeaf ? `level ${a.level}` : `level ${a.level} · group head`,
          group: a.type,
        })),
    [accounts, link.accountType, link.isControl],
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Combobox
        variant="field"
        className="min-w-[18rem]"
        value={code}
        onChange={setCode}
        options={options}
        placeholder="Search code or account name"
        caption={`${link.accountType.map((t) => t.toLowerCase()).join(' or ')} · ${link.isControl ? 'a head with room for sub-accounts' : 'postable leaf'}`}
        emptyText="No account of that type can take this link"
        aria-label={`Account for ${link.label}`}
      />
      <Button
        size="sm"
        variant="primary"
        loading={busy}
        disabled={code === '' || code === link.code}
        onClick={() => onChoose(code)}
      >
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
    </div>
  );
}
